import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { captureDocument as scanDocumentFromCamera } from '../services/camera';
import FormattedMessage from '../components/FormattedMessage';
import MessageAttachment from '../components/MessageAttachment';
import Sensitive2FAGate from '../components/Sensitive2FAGate';
import CustomerAuthorizations from '../components/CustomerAuthorizations';
import GrantorAccountView from '../components/GrantorAccountView';
import CustomerSidebar from '../components/customer/CustomerSidebar';
import CustomerTopBar from '../components/customer/CustomerTopBar';
import PremiumOverview from '../components/customer/PremiumOverview';
import PullToRefreshIndicator from '../components/PullToRefreshIndicator';
import usePullToRefresh from '../hooks/usePullToRefresh';
import { PageHeader, DataCard, ActionButton, StatusChip } from '../components/customer/ui';
import { formatDateUtc, formatMonthYearUtc } from '../utils/dateFormat';
import { compressAttachment } from '../services/compress';

const docsSeenKey = (userId) => `valueplus.docsLastSeenAt.${userId || 'anon'}`;
const getDocsLastSeenAt = (userId) => {
  try { return parseInt(localStorage.getItem(docsSeenKey(userId)) || '0', 10) || 0; }
  catch { return 0; }
};
const markDocsSeenNow = (userId) => {
  try { localStorage.setItem(docsSeenKey(userId), String(Date.now())); }
  catch (e) { console.warn('docs seen save failed', e); }
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [accruals, setAccruals] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [newDocuments, setNewDocuments] = useState(0);
  // Konto-Switcher: aktive Vollmachten, in denen aktueller User Bevollmächtigter ist
  const [activeGrantors, setActiveGrantors] = useState([]);
  const [viewingGrantorId, setViewingGrantorId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLoginBadge, setShowLoginBadge] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setShowLoginBadge(false), 6000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    loadDashboard();
    loadNotificationCounts();
    loadActiveGrantors();
  }, []);

  const loadActiveGrantors = async () => {
    try {
      const res = await axios.get(`${API}/account-authorizations`);
      const list = (res.data || []).filter(
        (a) => a.role_for_me === 'grantee' && a.status === 'active'
      );
      setActiveGrantors(list);
    } catch (e) {
      console.warn('Failed to load grantors', e);
    }
  };

  const loadNotificationCounts = async () => {
    try {
      const [msgRes, docRes] = await Promise.all([
        axios.get(`${API}/messages/unread-count`),
        axios.get(`${API}/customer/documents`),
      ]);
      setUnreadMessages(msgRes.data.count || 0);
      // Documents are "new" if uploaded after the user last visited the Documents tab.
      // First-time visitors see the last 7 days as new.
      const lastSeenMs = getDocsLastSeenAt(user?.id);
      const fallbackMs = Date.now() - 7 * 24 * 60 * 60 * 1000;
      const thresholdMs = lastSeenMs > 0 ? lastSeenMs : fallbackMs;
      const newDocs = (docRes.data || []).filter(d => new Date(d.created_at).getTime() > thresholdMs).length;
      setNewDocuments(newDocs);
    } catch (e) { console.warn('Badge count failed:', e); }
  };

  const loadDashboard = async () => {
    try {
      const [dashboardRes, accrualsRes, payoutsRes] = await Promise.all([
        axios.get(`${API}/customer/dashboard`),
        axios.get(`${API}/customer/accruals`),
        axios.get(`${API}/customer/payouts`),
      ]);
      
      setDashboard(dashboardRes.data);
      setAccruals(accrualsRes.data);
      setPayouts(payoutsRes.data);
    } catch (error) {
      toast.error('Fehler beim Laden des Dashboards');
    } finally {
      setLoading(false);
    }
  };

  // Mobile pull-to-refresh
  const pullToRefresh = usePullToRefresh({
    onRefresh: async () => {
      await Promise.all([loadDashboard(), loadNotificationCounts()]);
    },
    threshold: 80,
    maxPull: 140,
    enabled: !sidebarOpen,
  });

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const formatDate = (dateString) => formatDateUtc(dateString);

  const formatMonthYear = (dateString) => formatMonthYearUtc(dateString);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Fix: Calculate cumulative correctly after sorting
  const sortedAccruals = [...accruals].sort((a, b) => 
    new Date(a.period_start).getTime() - new Date(b.period_start).getTime()
  );
  
  let cumulativeSum = 0;
  const chartData = sortedAccruals.slice(-12).map((accrual) => {
    cumulativeSum += accrual.interest_amount_eur;
    return {
      month: formatMonthYear(accrual.period_start),
      zinsen: accrual.interest_amount_eur,
      kumuliert: cumulativeSum
    };
  });

  // Build a lookup so we can show the customer's own nickname on payouts and performance tabs
  const investmentsById = Object.fromEntries((dashboard?.investments || []).map((inv) => [inv.id, inv]));
  const investmentLabel = (inv) => inv?.nickname || inv?.product_code || 'Investment';

  return (
    <div
      className="min-h-screen bg-[#FAFAF5] relative"
      style={{
        // Content follows the finger when pulling, for that native rubber-band feel.
        transform: `translate3d(0, ${pullToRefresh.refreshing ? pullToRefresh.threshold * 0.5 : pullToRefresh.pullDistance * 0.5}px, 0)`,
        transition: pullToRefresh.pullDistance === 0 || pullToRefresh.refreshing
          ? 'transform 360ms cubic-bezier(0.22, 1, 0.36, 1)'
          : 'none',
      }}
    >
      <PullToRefreshIndicator
        pullDistance={pullToRefresh.pullDistance}
        refreshing={pullToRefresh.refreshing}
        progress={pullToRefresh.progress}
        threshold={pullToRefresh.threshold}
      />
      {/* Subtle warm grain overlay across the whole app */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.018] z-0"
        style={{ backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22200%22 height=%22200%22><filter id=%22n%22><feTurbulence type=%22fractalNoise%22 baseFrequency=%220.85%22 numOctaves=%222%22/></filter><rect width=%22200%22 height=%22200%22 filter=%22url(%23n)%22/></svg>")' }}
      />
      {/* Soft champagne gradient wash from top */}
      <div className="fixed top-0 right-0 w-[60vw] h-[40vh] pointer-events-none opacity-50 z-0"
        style={{ background: 'radial-gradient(circle at 80% 0%, rgba(197,160,89,0.10) 0%, transparent 60%)' }}
      />
      {/* Mobile sidebar — always mounted, animated via translate */}
      <div
        className={`lg:hidden fixed inset-0 bg-black/40 z-40 backdrop-blur-sm transition-opacity duration-500 ${sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={() => setSidebarOpen(false)}
        data-testid="sidebar-overlay"
      />
      <div
        className={`lg:hidden fixed inset-y-0 left-0 z-50 will-change-transform ${sidebarOpen ? 'translate-x-0 shadow-[14px_0_70px_-12px_rgba(0,0,0,0.55)]' : '-translate-x-full'}`}
        style={{
          transition: 'transform 720ms cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        <CustomerSidebar
          activeTab={activeTab}
          setActiveTab={(t) => {
            setActiveTab(t);
            if (t === 'messages') setUnreadMessages(0);
            if (t === 'documents') { markDocsSeenNow(user?.id); setNewDocuments(0); }
          }}
          badges={{ messages: unreadMessages, documents: newDocuments }}
          onLogout={handleLogout}
          onClose={() => setSidebarOpen(false)}
          animateItems={sidebarOpen}
        />
      </div>

      <div className="lg:flex">
        {/* Desktop sidebar — slides in once on mount */}
        <div className="hidden lg:block fixed inset-y-0 left-0 z-30 vp-anim-slide-soft">
          <CustomerSidebar
            activeTab={activeTab}
            setActiveTab={(t) => {
              setActiveTab(t);
              if (t === 'messages') setUnreadMessages(0);
              if (t === 'documents') { markDocsSeenNow(user?.id); setNewDocuments(0); }
            }}
            badges={{ messages: unreadMessages, documents: newDocuments }}
            onLogout={handleLogout}
          />
        </div>

        <div className="lg:ml-[260px] flex-1 min-w-0 min-h-screen flex flex-col">
          <CustomerTopBar
            user={user}
            notificationCount={unreadMessages}
            activeGrantors={activeGrantors}
            viewingGrantorId={viewingGrantorId}
            onSwitchGrantor={setViewingGrantorId}
            onMenu={() => setSidebarOpen(true)}
            showSuccessBadge={showLoginBadge}
          />

          {/* 2FA reminder banner */}
          {!(user?.twofa_enabled || user?.twofa_push_enabled) && (
            <div className="px-4 sm:px-8 pt-4">
              <Link
                to="/setup-2fa"
                className="inline-flex items-center gap-2 text-xs bg-amber-500 text-white px-3 py-1.5 rounded hover:bg-amber-600 transition"
                data-testid="setup-2fa-link"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                2FA jetzt einrichten
              </Link>
            </div>
          )}

          <main className="flex-1 px-4 sm:px-8 py-6 sm:py-10 max-w-[1600px]" data-testid="customer-dashboard">
            {viewingGrantorId ? (
              <GrantorAccountView
                grantorId={viewingGrantorId}
                onExit={() => setViewingGrantorId(null)}
              />
            ) : !dashboard?.investment && activeTab !== 'authorizations' ? (
              <div className="space-y-6">
                {activeGrantors.length > 0 && (
                  <div className="rounded-2xl bg-amber-50 border border-amber-200 p-5 flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-heading text-base text-[#0A0A0A] mb-1">Sie haben Lesezugriff auf andere Konten</h4>
                      <p className="text-sm text-gray-600">Wählen Sie oben rechts das gewünschte Konto aus, um die Investments einzusehen.</p>
                    </div>
                  </div>
                )}
                <div className="rounded-2xl bg-white border border-gray-100 text-center py-16 px-6">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center bg-[#F4ECD8]">
                    <svg className="w-8 h-8 text-[#C5A059]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="font-heading text-xl text-[#0A0A0A] mb-2">Noch kein Investment</h3>
                  <p className="text-sm text-gray-500">Ihr Investment wird hier angezeigt, sobald es aktiviert wurde.</p>
                </div>
              </div>
            ) : (
          <div key={activeTab} className="vp-anim-fade">
            {activeTab === 'overview' && (
              <PremiumOverview
                dashboard={dashboard}
                user={user}
                setActiveTab={(t) => {
                  setActiveTab(t);
                  if (t === 'messages') setUnreadMessages(0);
                  if (t === 'documents') { markDocsSeenNow(user?.id); setNewDocuments(0); }
                }}
                onCsvExport={() => { window.location.href = `${API}/export/accruals/csv`; }}
                onPdfExport={() => { window.location.href = `${API}/export/accruals/pdf`; }}
                onReload={loadDashboard}
              />
            )}

            {activeTab === 'performance' && (
              <div className="space-y-6 lg:space-y-8">
                <PageHeader
                  title="Zinsentwicklung"
                  subtitle="Visualisierung Ihrer Zinserträge im Zeitverlauf."
                  testid="performance-page-header"
                />

                {(dashboard?.investments || []).length > 0 && (
                  <DataCard title="Ihre Investments" testid="card-performance-investments">
                    <ul className="divide-y divide-gray-100">
                      {(dashboard.investments || []).map((inv) => (
                        <li key={inv.id} className="grid grid-cols-[1fr_auto_auto] items-center gap-4 py-3" data-testid={`performance-investment-${inv.id}`}>
                          <div className="min-w-0">
                            <p className="font-medium text-[#0A0A0A] truncate" data-testid={`performance-investment-name-${inv.id}`}>
                              {inv.nickname || inv.product_code}
                            </p>
                            {inv.nickname && (
                              <p className="text-[11px] text-gray-500 tabular-nums">{inv.product_code} · {(inv.apr * 100).toFixed(1)} % p.a.</p>
                            )}
                            {!inv.nickname && (
                              <p className="text-[11px] text-gray-500 tabular-nums">{(inv.apr * 100).toFixed(1)} % p.a.</p>
                            )}
                          </div>
                          <span className="text-sm text-[#0A0A0A] tabular-nums">{formatCurrency(inv.principal_eur)}</span>
                          <span className="text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">Aktiv</span>
                        </li>
                      ))}
                    </ul>
                  </DataCard>
                )}

                <DataCard title="Monatliche Zinsen" testid="card-monthly-interest">
                  <div className="overflow-x-auto">
                    <ResponsiveContainer width="100%" height={260} minWidth={300}>
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                        <XAxis dataKey="month" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={60} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip 
                          formatter={(value) => formatCurrency(value)}
                          contentStyle={{ backgroundColor: 'white', border: '1px solid #E4E4E7', borderRadius: '8px', fontSize: '12px' }}
                        />
                        <Bar dataKey="zinsen" fill="#059669" radius={[8, 8, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </DataCard>

                <DataCard title="Kumulierte Zinsen" testid="card-cumulative-interest">
                  <div className="overflow-x-auto">
                    <ResponsiveContainer width="100%" height={260} minWidth={300}>
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                        <XAxis dataKey="month" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={60} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip 
                          formatter={(value) => formatCurrency(value)}
                          contentStyle={{ backgroundColor: 'white', border: '1px solid #E4E4E7', borderRadius: '8px', fontSize: '12px' }}
                        />
                        <Line type="monotone" dataKey="kumuliert" stroke="#C5A059" strokeWidth={3} dot={{ r: 4 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </DataCard>
              </div>
            )}

            {activeTab === 'payouts' && (
              <div className="space-y-6 lg:space-y-8">
                <PageHeader
                  title="Auszahlungen"
                  subtitle={`${payouts.length} Auszahlungen gesamt – fällige und bereits bestätigte Beträge im Überblick.`}
                  action={
                    <a href={`${API}/export/payouts/csv`} data-testid="export-payouts-csv">
                      <ActionButton variant="secondary" size="md">CSV Export</ActionButton>
                    </a>
                  }
                  testid="payouts-page-header"
                />

                <DataCard padding="p-0" testid="payouts-list-card">
                  <ul className="divide-y divide-gray-100">
                    {payouts.map((payout) => (
                      <li
                        key={payout.id}
                        className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-3 sm:gap-6 items-center px-5 py-4 sm:py-5"
                        data-testid={`payout-detail-${payout.id}`}
                      >
                        <div>
                          <p className="font-heading text-lg sm:text-xl text-[#0A0A0A] tabular-nums">{formatCurrency(payout.amount_eur)}</p>
                          {investmentsById[payout.investment_id] && (
                            <p className="text-[11px] text-[#C5A059] mt-0.5" data-testid={`payout-investment-label-${payout.id}`}>
                              {investmentLabel(investmentsById[payout.investment_id])}
                            </p>
                          )}
                          <p className="text-xs text-gray-500 mt-1 tabular-nums">Fällig {formatDate(payout.due_date)}</p>
                          {payout.confirmed_at && (
                            <p className="text-[11px] text-emerald-700 mt-1 tabular-nums">
                              Bestätigt am {formatDate(payout.confirmed_at)}
                            </p>
                          )}
                          {payout.reference && (
                            <p className="text-[11px] text-gray-500 mt-1">Referenz: {payout.reference}</p>
                          )}
                        </div>
                        <div className="sm:text-right">
                          <StatusChip tone={payout.status === 'confirmed' ? 'confirmed' : 'due'}>
                            {payout.status === 'confirmed' ? 'Bestätigt' : 'Fällig'}
                          </StatusChip>
                        </div>
                        <div className="flex items-center gap-2 justify-end">
                          {payout.status === 'confirmed' && payout.documents && payout.documents.length > 0 && payout.documents.map((doc) => (
                            <a
                              key={doc.id}
                              href={`${API}/payout-documents/${doc.id}/download`}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`download-receipt-${doc.id}`}
                            >
                              <ActionButton variant="secondary" size="sm" icon={
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3"/></svg>
                              }>Beleg</ActionButton>
                            </a>
                          ))}
                        </div>
                      </li>
                    ))}
                    {payouts.length === 0 && (
                      <li className="p-12 text-center text-sm text-gray-500">Noch keine Auszahlungen vorhanden.</li>
                    )}
                  </ul>
                </DataCard>
              </div>
            )}

            {activeTab === 'documents' && (
              <div className="space-y-6 lg:space-y-8">
                <PageHeader
                  title="Dokumente"
                  subtitle="Alle Verträge, Bescheinigungen und Unterlagen zu Ihren Investments."
                  testid="documents-page-header"
                />
                <Sensitive2FAGate user={user} tabName="documents" label="Dokumente">
                  <CustomerDocuments userId={dashboard?.user?.id} />
                </Sensitive2FAGate>
              </div>
            )}

            {activeTab === 'messages' && (
              <div className="space-y-6 lg:space-y-8">
                <PageHeader
                  title="Postfach"
                  subtitle="Nachrichten und Mitteilungen von Value Plus."
                  testid="messages-page-header"
                />
                <Sensitive2FAGate user={user} tabName="messages" label="Postfach">
                  <CustomerMessages userId={dashboard?.user?.id} />
                </Sensitive2FAGate>
              </div>
            )}

            {activeTab === 'bank' && (
              <div className="space-y-6 lg:space-y-8">
                <PageHeader
                  title="Bankverbindung"
                  subtitle="Ihre hinterlegten Kontodaten für Auszahlungen. Änderungen können nur durch Ihren persönlichen Berater vorgenommen werden."
                  testid="bank-page-header"
                />

                <div className="rounded-2xl p-6 sm:p-7 text-white relative overflow-hidden" style={{ background: 'linear-gradient(145deg, #0A0A0A 0%, #1a1a1a 100%)' }}>
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(197, 160, 89, 0.2)' }}>
                        <svg className="w-6 h-6" style={{ color: '#C5A059' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-white font-semibold">Auszahlungskonto</p>
                        <p className="text-gray-400 text-xs">Verifiziert</p>
                      </div>
                    </div>
                    <div className="px-3 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: 'rgba(74, 222, 128, 0.2)', color: '#4ade80' }}>
                      Aktiv
                    </div>
                  </div>

                  {user?.bank_iban ? (
                    <div className="space-y-4">
                      <div className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                        <p className="text-gray-400 text-xs mb-1">Kontoinhaber</p>
                        <p className="text-white font-medium text-lg">{user.bank_account_holder || user.name || '-'}</p>
                      </div>
                      
                      <div className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                        <p className="text-gray-400 text-xs mb-1">IBAN</p>
                        <p className="text-white font-mono text-lg tracking-wider">
                          {user.bank_iban.replace(/(.{4})/g, '$1 ').trim()}
                        </p>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                          <p className="text-gray-400 text-xs mb-1">BIC</p>
                          <p className="text-white font-mono">{user.bank_bic || '-'}</p>
                        </div>
                        <div className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                          <p className="text-gray-400 text-xs mb-1">Bank</p>
                          <p className="text-white">{user.bank_name || '-'}</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      </div>
                      <p className="text-gray-400 mb-2">Keine Bankverbindung hinterlegt</p>
                      <p className="text-gray-500 text-sm">Bitte kontaktieren Sie Ihren Berater, um Ihre Bankdaten zu hinterlegen.</p>
                    </div>
                  )}
                </div>

                <div className="rounded-2xl bg-white border border-gray-100 p-5 sm:p-6">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-[#F4ECD8]">
                      <svg className="w-5 h-5 text-[#C5A059]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-heading text-base text-[#0A0A0A] mb-1">Wichtiger Hinweis</h4>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        Alle Auszahlungen werden ausschließlich auf das hier hinterlegte Konto überwiesen. 
                        Änderungen an den Bankdaten können nur durch Ihren persönlichen Berater vorgenommen werden.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'authorizations' && (
              <CustomerAuthorizations currentUser={user} />
            )}
          </div>
        )}
          </main>
        </div>
      </div>
    </div>
  );
};

const CustomerDocuments = ({ userId }) => {
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showUploadMenu, setShowUploadMenu] = useState(false);

  useEffect(() => { loadDocuments(); }, []);

  const loadDocuments = async () => {
    try {
      const res = await axios.get(`${API}/customer/documents`);
      setDocuments(res.data);
    } catch (e) { console.error(e); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!allowed.includes(file.type)) { toast.error('Nur PDF, JPG und PNG erlaubt'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Max. 10MB'); return; }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/customer/documents/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Dokument hochgeladen');
      loadDocuments();
    } catch (err) {
      toast.error('Fehler beim Hochladen');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleCameraScan = async () => {
    setShowUploadMenu(false);
    try {
      const file = await scanDocumentFromCamera();
      if (!file) return;
      
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/customer/documents/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Dokument gescannt und hochgeladen');
      loadDocuments();
    } catch (err) {
      toast.error('Fehler beim Scannen');
    } finally {
      setUploading(false);
    }
  };

  const formatDate = (d) => formatDateUtc(d);

  return (
    <div className="space-y-6" data-testid="customer-documents">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-heading font-bold mb-2">Dokumente</h2>
          <p className="text-muted-foreground text-sm">{documents.length} Dokumente</p>
        </div>
        <div className="relative">
          <button 
            onClick={() => setShowUploadMenu(!showUploadMenu)}
            className="btn-primary text-sm flex items-center gap-2"
            disabled={uploading}
            data-testid="upload-document-button"
          >
            {uploading ? (
              <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Wird hochgeladen...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Dokument hochladen
              </>
            )}
          </button>
          {showUploadMenu && !uploading && (
            <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50" style={{ animation: 'fadeInUp 0.2s ease-out' }}>
              <label className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors">
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="text-sm text-gray-700">Datei auswählen</span>
                <input type="file" className="hidden" onChange={(e) => { setShowUploadMenu(false); handleUpload(e); }} accept=".pdf,.jpg,.jpeg,.png" />
              </label>
              <button 
                onClick={handleCameraScan}
                className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 w-full text-left transition-colors"
                data-testid="camera-scan-button"
              >
                <svg className="w-5 h-5 text-[#C5A059]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="text-sm text-gray-700">Dokument scannen</span>
              </button>
            </div>
          )}
        </div>
      </div>
      
      {documents.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-muted-foreground">Noch keine Dokumente vorhanden</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {documents.map((doc) => (
            <div key={doc.id} className="card flex justify-between items-center" data-testid={`document-${doc.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-primary/10">
                  <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-sm">{doc.title}</p>
                  <p className="text-xs text-muted-foreground">{formatDate(doc.created_at)}</p>
                </div>
              </div>
              <a href={`${API}/documents/${doc.id}/download`} className="text-accent hover:underline text-sm" data-testid={`download-doc-${doc.id}`}>
                Herunterladen
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CustomerMessages = ({ userId }) => {
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [showCompose, setShowCompose] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [attachmentOriginalSize, setAttachmentOriginalSize] = useState(0);
  const [compressing, setCompressing] = useState(false);
  const [sending, setSending] = useState(false);
  const [adminId, setAdminId] = useState(null);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    loadMessages();
    axios.get(`${API}/messages/unread-count`).then(r => setUnread(r.data.count)).catch(() => {});
    // Find admin ID from dedicated endpoint
    axios.get(`${API}/messages/admins`).then(r => {
      if (r.data && r.data.length > 0) setAdminId(r.data[0].id);
    }).catch(() => {
      // Fallback: try to find admin from existing messages
      axios.get(`${API}/messages`).then(r => {
        const adminMsg = r.data.find(m => m.sender_role === 'ADMIN');
        if (adminMsg) setAdminId(adminMsg.sender_id);
      }).catch(() => {});
    });
  }, []);

  const loadMessages = async () => {
    try {
      const res = await axios.get(`${API}/messages`);
      setMessages(res.data);
    } catch (e) { console.error(e); }
  };

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/messages/${id}/read`);
      setMessages(msgs => msgs.map(m => m.id === id ? { ...m, read_at: new Date().toISOString() } : m));
      setUnread(prev => Math.max(0, prev - 1));
    } catch (e) { console.error(e); }
  };

  const acceptFile = async (raw) => {
    if (!raw) return;
    const allowed = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!allowed.includes(raw.type)) { toast.error('Nur PDF, JPG und PNG erlaubt'); return; }
    const originalSize = raw.size;
    setCompressing(true);
    try {
      const compressed = await compressAttachment(raw);
      if (compressed.size > 10 * 1024 * 1024) {
        toast.error('Datei ist auch nach Komprimierung über 10 MB');
        return;
      }
      setAttachment(compressed);
      setAttachmentOriginalSize(originalSize);
      if (compressed.size < originalSize * 0.95) {
        const orig = (originalSize / 1024 / 1024).toFixed(1);
        const comp = (compressed.size / 1024 / 1024).toFixed(1);
        toast.success(`Komprimiert: ${orig} MB → ${comp} MB`);
      }
    } catch (e) {
      console.error(e);
      toast.error('Komprimierung fehlgeschlagen');
    } finally {
      setCompressing(false);
    }
  };

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    acceptFile(f);
  };

  const scanAttachment = async () => {
    try {
      const f = await scanDocumentFromCamera('camera');
      if (!f) return;
      await acceptFile(f);
    } catch (e) {
      toast.error('Fehler beim Scannen');
    }
  };

  const sendMessage = async () => {
    if (!subject.trim() || !body.trim()) { toast.error('Betreff und Nachricht erforderlich'); return; }
    if (!adminId) { toast.error('Admin-Empfänger nicht gefunden. Bitte versuchen Sie es erneut.'); return; }
    setSending(true);
    try {
      const form = new FormData();
      form.append('recipient_id', String(adminId));
      form.append('subject', subject);
      form.append('body', body);
      if (attachment) form.append('file', attachment);
      await axios.post(`${API}/messages`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Nachricht gesendet');
      setShowCompose(false);
      setSubject('');
      setBody('');
      setAttachment(null);
      setAttachmentOriginalSize(0);
      loadMessages();
    } catch (err) {
      toast.error('Fehler beim Senden: ' + (err.response?.data?.detail || 'Unbekannter Fehler'));
    } finally {
      setSending(false);
    }
  };

  const formatDate = (d) => format(new Date(d), 'dd.MM.yyyy HH:mm', { locale: de });

  return (
    <div className="space-y-6" data-testid="customer-messages">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-heading font-bold mb-2">Postfach</h2>
          <p className="text-muted-foreground text-sm">
            {messages.length} Nachrichten{unread > 0 && `, ${unread} ungelesen`}
          </p>
        </div>
        <button onClick={() => setShowCompose(!showCompose)} className="btn-primary text-sm" data-testid="compose-message-button">
          Neue Nachricht
        </button>
      </div>

      {showCompose && (
        <div className="card space-y-4" data-testid="compose-form">
          <input
            type="text" className="input-field" placeholder="Betreff..."
            value={subject} onChange={e => setSubject(e.target.value)} data-testid="message-subject-input"
          />
          <textarea
            className="input-field" rows={4} placeholder="Ihre Nachricht..."
            value={body} onChange={e => setBody(e.target.value)} data-testid="message-body-input"
          />

          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileInputRef} type="file" className="hidden"
              accept=".pdf,.jpg,.jpeg,.png" onChange={pickFile}
              data-testid="message-file-input"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={compressing}
              className="text-sm flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition disabled:opacity-50"
              data-testid="message-attach-file-button"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
              Datei anhängen
            </button>
            <button
              type="button"
              onClick={scanAttachment}
              disabled={compressing}
              className="text-sm flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition disabled:opacity-50"
              data-testid="message-scan-button"
            >
              <svg className="w-4 h-4" style={{ color: '#C5A059' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Scannen
            </button>
            {compressing && (
              <span className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="message-compressing">
                <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
                Wird komprimiert...
              </span>
            )}
            {attachment && !compressing && (
              <div
                className="flex items-center gap-2 text-xs px-2 py-1 rounded-md bg-accent/10 text-primary"
                data-testid="message-attachment-preview"
              >
                <span className="truncate" style={{ maxWidth: '180px' }}>{attachment.name}</span>
                <span className="text-muted-foreground">
                  {(attachment.size / 1024 / 1024).toFixed(2)} MB
                  {attachmentOriginalSize > attachment.size * 1.05 && (
                    <> · <span className="line-through">{(attachmentOriginalSize / 1024 / 1024).toFixed(1)} MB</span></>
                  )}
                </span>
                <button
                  type="button"
                  onClick={() => { setAttachment(null); setAttachmentOriginalSize(0); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                  className="text-gray-500 hover:text-red-600"
                  aria-label="Anhang entfernen"
                  data-testid="message-remove-attachment"
                >
                  ✕
                </button>
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <button onClick={() => { setShowCompose(false); setAttachment(null); setAttachmentOriginalSize(0); }} className="btn-secondary flex-1">Abbrechen</button>
            <button onClick={sendMessage} disabled={sending || compressing} className="btn-primary flex-1" data-testid="send-message-button">
              {sending ? 'Wird gesendet...' : 'Senden'}
            </button>
          </div>
        </div>
      )}

      {messages.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-muted-foreground">Noch keine Nachrichten</p>
        </div>
      ) : (
        <div className="space-y-3">
          {messages.map(msg => (
            <div
              key={msg.id}
              className={`card cursor-pointer transition hover:shadow-md ${!msg.read_at && !msg.is_mine ? 'border-l-4 border-l-accent bg-accent/5' : ''}`}
              onClick={() => !msg.read_at && !msg.is_mine && markRead(msg.id)}
              data-testid={`message-${msg.id}`}
            >
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${msg.is_mine ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
                    {msg.is_mine ? 'Gesendet' : msg.sender_name}
                  </span>
                  {!msg.read_at && !msg.is_mine && (
                    <span className="w-2 h-2 rounded-full bg-accent"></span>
                  )}
                </div>
                <span className="text-xs text-muted-foreground">{formatDate(msg.created_at)}</span>
              </div>
              <p className="font-medium text-sm">{msg.subject}</p>
              <FormattedMessage text={msg.body} className="text-sm text-muted-foreground mt-1" />
              <MessageAttachment message={msg} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomerDashboard;
